import * as React from 'react';

import { cn } from '../../lib/stylingUtils';

export interface ImageProps {
  /**
   * Data URI for the image
   * Format: "data:image/[format];base64,[data]"
   */
  src: string;
  /**
   * Alternative text for accessibility
   */
  alt?: string;
  /**
   * Image format (png, svg, jpg)
   */
  format?: string;
  /**
   * Whether this is an inline image (vs block)
   */
  inline?: boolean;
  /**
   * Optional className for styling
   */
  className?: string;
}

/**
 * Validate data URI format
 */
function validateDataUri(src: string): boolean {
  return src.startsWith('data:image/');
}

/**
 * ImageInline component for inline images within text
 *
 * Renders as an inline <img> element with appropriate sizing
 *
 * @example
 * ```tsx
 * <ImageInline
 *   src="data:image/png;base64,iVBOR..."
 *   alt="Chart"
 * />
 * ```
 */
export const ImageInline: React.FC<ImageProps> = ({
  src,
  alt = '',
  className,
}) => {
  if (!validateDataUri(src)) {
    return null;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn('@ImageInline', className)}
      style={{
        display: 'inline-block',
        maxWidth: '100%',
        maxHeight: '2em',
        verticalAlign: 'middle',
        margin: '0 0.25em',
      }}
      loading="lazy"
    />
  );
};

ImageInline.displayName = 'ImageInline';

/**
 * ImageBlock component for block-level images
 *
 * Renders as a <figure> element with optional caption
 * Provides better layout and spacing for standalone images
 *
 * @example
 * ```tsx
 * <ImageBlock
 *   src="data:image/png;base64,iVBOR..."
 *   alt="Revenue Chart"
 *   format="png"
 * />
 * ```
 */
export const ImageBlock: React.FC<ImageProps> = ({
  src,
  alt = '',
  format: _format,
  className,
}) => {
  if (!validateDataUri(src)) {
    return null;
  }

  return (
    <span
      className={cn('@ImageBlock', className)}
      style={{ display: 'inline-block', width: '100%', margin: '1.5em 0' }}
    >
      <span
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: '0.5rem',
          padding: '1rem',
          border: '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <img
          src={src}
          alt={alt}
          style={{
            maxWidth: '100%',
            maxHeight: '500px',
            height: 'auto',
            display: 'block',
          }}
          loading="lazy"
        />
      </span>
      {alt && (
        <span
          style={{
            display: 'block',
            marginTop: '0.75rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'rgba(0, 0, 0, 0.6)',
            fontStyle: 'italic',
          }}
        >
          {alt}
        </span>
      )}
    </span>
  );
};

ImageBlock.displayName = 'ImageBlock';
